
(() => {
  if (window.__ellaProductDebugInstalled) return;
  window.__ellaProductDebugInstalled = true;

  const startedAt = performance.now();
  const log = (event, details = {}) => {
    console.log(
      `[EllaDebug +${((performance.now() - startedAt) / 1000).toFixed(3)}s] ${event}`,
      details
    );
  };

  const isProductBlockRequest = url =>
    /ajax_product_block/i.test(String(url || ""));

  const getSection = element =>
    element?.closest?.("[data-product-block]");

  const getSectionName = section =>
    section?.querySelector(".halo-block-header .title")?.textContent?.trim()
    || section?.dataset?.collection
    || section?.id
    || "Unknown product section";

  // Record when product images are inserted and when they finish loading.
  const watchedImages = new WeakSet();

  function watchImages(root) {
    if (!root || root.nodeType !== 1) return;

    const images = [];
    if (root.matches?.("img")) images.push(root);
    root.querySelectorAll?.("img").forEach(img => images.push(img));

    images.forEach(img => {
      if (watchedImages.has(img)) return;
      watchedImages.add(img);

      const section = getSection(img);
      const details = () => ({
        section: getSectionName(section),
        alt: img.alt,
        url: img.currentSrc || img.src || img.dataset.src || "",
        naturalWidth: img.naturalWidth,
        complete: img.complete,
        className: img.className
      });

      log("IMAGE INSERTED", details());

      img.addEventListener("load", () => {
        log("IMAGE LOADED", details());
      }, { once: true });

      img.addEventListener("error", () => {
        log("IMAGE ERROR", details());
      }, { once: true });

      if (img.complete) {
        log(img.naturalWidth ? "IMAGE ALREADY LOADED" : "IMAGE ALREADY FAILED",
          details());
      }
    });
  }

  // Observe changes to product sections and image attributes.
  const observer = new MutationObserver(records => {
    for (const record of records) {
      const target = record.target.nodeType === 1
        ? record.target
        : record.target.parentElement;

      const section = getSection(target);

      if (section && record.type === "childList") {
        log("PRODUCT SECTION DOM CHANGED", {
          section: getSectionName(section),
          addedNodes: record.addedNodes.length,
          removedNodes: record.removedNodes.length,
          className: section.className
        });
      }

      record.addedNodes?.forEach(node => {
        if (node.nodeType === 1) watchImages(node);
      });

      if (record.type === "attributes" && target?.matches?.("img")) {
        log("IMAGE ATTRIBUTE CHANGED", {
          attribute: record.attributeName,
          url: target.currentSrc || target.src || target.dataset.src || "",
          className: target.className
        });
      }
    }
  });

  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["src", "srcset", "data-src", "class"]
  });

  // Instrument XHR, including jQuery AJAX, before the theme makes requests.
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this.__ellaDebugRequest = {
      method,
      url: String(url),
      tracked: isProductBlockRequest(url)
    };
    return originalOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function(...args) {
    const info = this.__ellaDebugRequest;

    if (info?.tracked) {
      const requestStart = performance.now();
      log("AJAX START", info);

      this.addEventListener("loadend", () => {
        log("AJAX END", {
          ...info,
          status: this.status,
          elapsedMs: Math.round(performance.now() - requestStart),
          responseBytes: this.responseText?.length ?? null
        });

        if (this.status < 200 || this.status >= 300) {
          log("AJAX NON-SUCCESS STATUS", {
            url: info.url,
            status: this.status
          });
        }
      }, { once: true });
    }

    return originalSend.apply(this, args);
  };

  // Record resource timing for matching requests.
  const reported = new Set();

  function reportResources() {
    performance.getEntriesByType("resource")
      .filter(entry => isProductBlockRequest(entry.name))
      .forEach(entry => {
        const key = entry.name + "|" + entry.startTime;
        if (reported.has(key)) return;
        reported.add(key);

        log("AJAX RESOURCE TIMING", {
          url: entry.name,
          startMs: Math.round(entry.startTime),
          durationMs: Math.round(entry.duration),
          transferBytes: entry.transferSize,
          responseBytes: entry.encodedBodySize
        });
      });
  }

  try {
    new PerformanceObserver(reportResources)
      .observe({ type: "resource", buffered: true });
  } catch (error) {
    log("RESOURCE OBSERVER UNAVAILABLE", { message: error.message });
  }

  document.querySelectorAll("[data-product-block]").forEach(section => {
    log("PRODUCT SECTION FOUND", {
      section: getSectionName(section),
      id: section.id,
      collection: section.dataset.collection,
      layout: section.dataset.layout,
      className: section.className
    });
    watchImages(section);
  });

  window.addEventListener("load", () => {
    log("WINDOW LOAD");
    reportResources();
  });

  log("DIAGNOSTICS INSTALLED");
})();
